import { sanitizeHtml } from "../../sanitizeHtml";

const SKIP_TAGS = new Set(["STYLE", "META", "LINK", "SCRIPT", "TITLE", "XML", "HEAD"]);
const WRAPPER_TAGS = new Set(["HTML", "BODY"]);
const BLOCK_TAGS = new Set([
    "P",
    "DIV",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "BLOCKQUOTE",
    "SECTION",
    "ARTICLE",
    "PRE",
    "TR",
]);

export type PastedSegment =
    | { type: "body"; html: string }
    | { type: "listItem"; html: string; ordered: boolean; depth: number };

export function splitClipboardIntoBlocks(html: string, text: string): PastedSegment[] {
    if (html.trim()) {
        const fromHtml = promoteListPrefixes(collectNodes(parseHtml(html)));
        if (fromHtml.length > 0) return fromHtml;
    }
    return splitTextIntoBlocks(text);
}

function parseHtml(html: string): ParentNode {
    const template = document.createElement("template");
    template.innerHTML = html.replace(/<!--[\s\S]*?-->/g, "");
    return template.content;
}

function collectNodes(root: ParentNode): PastedSegment[] {
    const segments: PastedSegment[] = [];
    let buffer = document.createElement("div");

    const flushBody = () => {
        const html = sanitizeHtml(buffer.innerHTML);
        if (isMeaningfulHtml(html)) segments.push({ type: "body", html });
        buffer = document.createElement("div");
    };

    for (const child of Array.from(root.childNodes)) {
        if (child instanceof HTMLElement && SKIP_TAGS.has(child.tagName)) continue;

        if (child instanceof HTMLElement && WRAPPER_TAGS.has(child.tagName)) {
            flushBody();
            segments.push(...collectNodes(child));
            continue;
        }

        if (child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")) {
            flushBody();
            segments.push(...collectList(child, 0));
            continue;
        }

        if (child instanceof HTMLElement && child.tagName === "LI") {
            flushBody();
            segments.push(...collectListItem(child, false, 0));
            continue;
        }

        if (child instanceof HTMLElement && child.tagName === "BR") {
            flushBody();
            continue;
        }

        if (child instanceof HTMLElement && BLOCK_TAGS.has(child.tagName)) {
            flushBody();
            segments.push(...collectNodes(child));
            continue;
        }

        if (child.nodeType === Node.TEXT_NODE) {
            const lines = (child.textContent ?? "").split(/\r?\n/);
            lines.forEach((line, index) => {
                if (index > 0) flushBody();
                if (line) buffer.appendChild(document.createTextNode(line));
            });
            continue;
        }

        buffer.appendChild(child.cloneNode(true));
    }

    flushBody();
    return segments;
}

function collectList(list: HTMLElement, depth: number): PastedSegment[] {
    const ordered = list.tagName === "OL";
    const segments: PastedSegment[] = [];
    for (const child of Array.from(list.children)) {
        if (!(child instanceof HTMLElement)) continue;
        if (child.tagName === "UL" || child.tagName === "OL") {
            segments.push(...collectList(child, depth + 1));
            continue;
        }
        if (child.tagName === "LI") {
            segments.push(...collectListItem(child, ordered, depth));
        }
    }
    return segments;
}

function collectListItem(item: HTMLElement, ordered: boolean, depth: number): PastedSegment[] {
    const segments: PastedSegment[] = [];
    const inline = document.createElement("div");
    for (const child of Array.from(item.childNodes)) {
        if (child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")) {
            pushListItem(segments, inline, ordered, depth);
            segments.push(...collectList(child, depth + 1));
            continue;
        }
        inline.appendChild(child.cloneNode(true));
    }
    pushListItem(segments, inline, ordered, depth);
    return segments;
}

function pushListItem(
    segments: PastedSegment[],
    inline: HTMLElement,
    ordered: boolean,
    depth: number,
) {
    const html = sanitizeHtml(inline.innerHTML);
    inline.innerHTML = "";
    if (!isMeaningfulHtml(html)) return;
    segments.push({ type: "listItem", html, ordered, depth });
}

function promoteListPrefixes(segments: PastedSegment[]): PastedSegment[] {
    const next: PastedSegment[] = [];
    for (const segment of segments) {
        if (segment.type !== "body") {
            next.push(segment);
            continue;
        }
        const parsed = parseListLine(htmlToPlainText(segment.html));
        if (!parsed) {
            next.push(segment);
            continue;
        }
        next.push({
            type: "listItem",
            html: stripListPrefix(segment.html, parsed.prefixLength),
            ordered: parsed.ordered,
            depth: parsed.depth,
        });
    }
    return next;
}

function splitTextIntoBlocks(text: string): PastedSegment[] {
    const segments: PastedSegment[] = [];
    for (const line of text.split(/\r?\n/)) {
        const parsed = parseListLine(line);
        if (parsed) {
            segments.push({
                type: "listItem",
                html: sanitizeHtml(escapeText(line.slice(parsed.prefixLength))),
                ordered: parsed.ordered,
                depth: parsed.depth,
            });
            continue;
        }
        const html = escapeText(line);
        if (isMeaningfulHtml(html)) segments.push({ type: "body", html });
    }
    return segments;
}

function parseListLine(line: string) {
    const indent = line.match(/^[ \t]*/)?.[0] ?? "";
    const rest = line.slice(indent.length);
    const depth = Math.min(3, Math.floor(indent.replace(/\t/g, "  ").length / 2));
    const unordered = rest.match(/^[-*•]\s+/);
    if (unordered) {
        return {
            type: "listItem" as const,
            ordered: false,
            depth,
            prefixLength: indent.length + unordered[0].length,
        };
    }
    const numbered = rest.match(/^\d+[.)]\s+/);
    if (numbered) {
        return {
            type: "listItem" as const,
            ordered: true,
            depth,
            prefixLength: indent.length + numbered[0].length,
        };
    }
    return null;
}

function stripListPrefix(html: string, prefixLength: number): string {
    const template = document.createElement("template");
    template.innerHTML = html;
    let remaining = prefixLength;
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const toRemove: Text[] = [];
    while (remaining > 0 && walker.nextNode()) {
        const node = walker.currentNode as Text;
        if (node.data.length <= remaining) {
            remaining -= node.data.length;
            toRemove.push(node);
        } else {
            node.data = node.data.slice(remaining);
            remaining = 0;
        }
    }
    for (const node of toRemove) node.remove();
    return sanitizeHtml(template.innerHTML);
}

function htmlToPlainText(html: string): string {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content.textContent ?? "";
}

export function isMeaningfulHtml(html: string): boolean {
    const template = document.createElement("template");
    template.innerHTML = html;
    return (template.content.textContent ?? "").replace(/\u00a0/g, " ").trim() !== "";
}

function escapeText(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
